import type { FC } from "react"
import React from "react"

import type { Rectangle2d } from "@logic/lib/2d"
import { toRadians } from "@logic/lib/2d/toRadians"
import type { Panel } from "@logic/lib/design"
import { placePotentialPanels } from "@logic/lib/design"
import { useRecoilValue, useSetRecoilState } from "recoil"
import styled from "styled-components"

import { toolState } from "~/containers/LegacyDesigner/state/tool"

import { HatchPattern } from "./Patterns"
import {
  findPanelSlope,
  findProjectedPanelState,
  panelGridGapState,
} from "../../../state/elements/panels"
import { useAddOnePanel } from "../../../state/elements/transactions/addPanel"
import { shouldShowPreviewState } from "../../Overlays/PreviewPanel"

const PotentialPanel = ({
  x,
  y,
  w,
  h,
  r,
  onClick,
}: Rectangle2d & {
  orientation?: number | null
  onClick: () => void
  gridGap: number
}) => {
  const showPreview = useSetRecoilState(shouldShowPreviewState)

  return (
    <>
      <HatchPattern id="potential" />
      <StyledPanel
        style={{ transformBox: `fill-box` }}
        transform={`rotate(${r})`}
        onClick={onClick}
        onMouseEnter={() => showPreview(false)}
        onMouseLeave={() => showPreview(true)}
        x={x}
        y={y}
        width={w}
        height={h}
        r={6}
        fill="transparent"
      />
    </>
  )
}

const PotentialPanels_Internal: FC<{ id: string }> = ({ id }) => {
  const panel = useRecoilValue(findProjectedPanelState(id))
  const { w, h, orientation } = panel
  const slope = useRecoilValue(findPanelSlope(id))
  const addPanel = useAddOnePanel()
  const gridGap = useRecoilValue(panelGridGapState)
  const tool = useRecoilValue(toolState)

  const bothAreVertical =
    tool === `PanelCreatorPortrait` &&
    [null, undefined, 0, 180].includes(orientation)
  const bothAreHorizontal =
    tool === `PanelCreatorLandscape` && [90, 270].includes(orientation as number)
  const isAligned = bothAreVertical || bothAreHorizontal

  const isPortrait = tool === `PanelCreatorPortrait`

  const foreshorteningFactor = Math.cos(toRadians(slope))
  const scale = {
    h: isAligned
      ? h
      : isPortrait
        ? h * foreshorteningFactor
        : h / foreshorteningFactor,
    w: isAligned
      ? w
      : isPortrait
        ? w / foreshorteningFactor
        : w * foreshorteningFactor,
  }

  const potentialPanelPositions = placePotentialPanels({
    originalPanel: panel,
    creatingPanel: { ...panel, ...scale },
    isAligned,
    isLandscape: tool === `PanelCreatorLandscape`,
  })

  return (
    <g>
      {potentialPanelPositions.map((point, index) => {
        const isPortrait = tool === `PanelCreatorPortrait`
        const createdOrientation = isPortrait ? 0 : 90
        const rotationOffset = isPortrait ? -90 : 90
        const createdRotation = isAligned ? panel.r : panel.r + rotationOffset
        const newPanel: Panel = {
          ...panel,
          ...scale,
          ...point,
          r: createdRotation,
          orientation: createdOrientation,
        }

        return (
          <PotentialPanel
            key={index + id}
            {...newPanel}
            gridGap={gridGap}
            onClick={() => addPanel(newPanel)}
          />
        )
      })}
    </g>
  )
}

export const PotentialPanels = React.memo(PotentialPanels_Internal)

const StyledPanel = styled.rect`
  transition: fill 0.2s ease-in-out;
  opacity: 0.5;
  &:hover {
    fill: url(#pattern-hatch-potential);
  }
`
